import {Tooltip} from '@dropbox/dig-components/tooltips';
import {Truncate} from '@dropbox/dig-components/truncate';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, ThemeProvider} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {PersonMultipleLine} from '@dropbox/dig-icons/assets';
import {derivedThemeAtom} from 'atoms/layout';
import type {EmployeeRoleLevel} from 'client';
import {Avatar} from 'components/DSYS/Avatar';
import {useAtomValue} from 'jotai';
import {Fragment, useRef, useState} from 'react';

// Long names truncate so the properties line stays predictable.
const NAME_MAX_WIDTH_PX = 180;
const PEEK_MAX_WIDTH_PX = 360;
const PEEK_NAME_MAX_WIDTH_PX = 200;
// Just enough that sweeping across the properties line doesn't flash the peek.
const PEEK_OPEN_DELAY_MS = 100;

export type SidebarMemberRole = {
  label: string;
  person: EmployeeRoleLevel;
};

/** Hover peek: role | person rows, labels in one column so the names line up. */
const MembersPeek = ({roles}: {roles: SidebarMemberRole[]}) => (
  <Box
    alignItems="center"
    style={{
      display: 'grid',
      gridTemplateColumns: 'max-content 1fr',
      columnGap: 16,
      rowGap: 10,
    }}
  >
    {roles.map(({label, person}) => (
      <Fragment key={label}>
        <Text size="small" color="subtle">
          {label}
        </Text>
        <Box display="flex" alignItems="center" style={{gap: 8}}>
          <Avatar user={person} size="xsmall" />
          <Text size="small">
            <Truncate maxWidth={PEEK_NAME_MAX_WIDTH_PX}>{person.name}</Truncate>
          </Text>
        </Box>
      </Fragment>
    ))}
  </Box>
);

/**
 * Properties-row people control, shaped like a split button: the DRI | member
 * count. Hovering peeks at who holds each role; clicking opens the full members
 * list (where roles are edited). The hairline always shows, so the shape doesn't
 * shift on hover.
 */
export const SidebarDriSplitButton = ({
  dri,
  noDriLabel,
  memberCount,
  roles,
  onViewMembers,
}: {
  dri?: EmployeeRoleLevel;
  noDriLabel: string;
  memberCount: number;
  roles: SidebarMemberRole[];
  onViewMembers: () => void;
}) => {
  const [hovered, setHovered] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const theme = useAtomValue(derivedThemeAtom);
  return (
    <>
      {/* Tooltips invert their theme; flipping it here lands the peek on the page's
          own theme, since the inverse card glares in dark mode. */}
      <ThemeProvider mode={theme === 'dark' ? 'bright' : 'dark'}>
        <Tooltip.Control
          variant="rich"
          open={hovered}
          triggerRef={triggerRef}
          placement="bottom-start"
          auto
          openDelay={PEEK_OPEN_DELAY_MS}
          maxWidth={PEEK_MAX_WIDTH_PX}
          shouldDisablePointerEvents
          // Hover-only, so focus never moves in; returning it on close would
          // focus the button (and ring it) every time the mouse leaves.
          shouldReturnFocus={false}
        >
          {roles.length > 0 ? (
            <MembersPeek roles={roles} />
          ) : (
            <Tooltip.Text size="small">View all members</Tooltip.Text>
          )}
        </Tooltip.Control>
      </ThemeProvider>
      <button
        ref={triggerRef}
        type="button"
        aria-label={`${dri?.name ?? noDriLabel} · view all ${memberCount} members`}
        onClick={() => {
          setHovered(false);
          onViewMembers();
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onMouseDown={(event) => event.preventDefault()}
        className={atoms({
          display: 'flex',
          alignItems: 'center',
          borderRadius: 'Medium',
          cursor: 'pointer',
          color: 'inherit',
          backgroundColor: hovered ? 'Opacity Surface - State 1' : 'transparent',
        })}
        style={{height: 32, padding: 0, border: 0}}
      >
        <Box display="flex" alignItems="center" paddingX="8" style={{gap: 6}}>
          {dri ? <Avatar user={dri} size="xsmall" /> : null}
          {/* Regular weight, like the other properties (Public, resources). */}
          <Text size="small" color={dri ? 'standard' : 'subtle'}>
            <Truncate maxWidth={NAME_MAX_WIDTH_PX}>{dri?.name ?? noDriLabel}</Truncate>
          </Text>
        </Box>
        <Box backgroundColor="Border Subtle" style={{width: 1, height: 16}} />
        <Box display="flex" alignItems="center" paddingX="8" style={{gap: 6}}>
          <UIIcon src={PersonMultipleLine} size="small" className={atoms({color: 'Text Subtle'})} />
          <Text size="small">{memberCount}</Text>
        </Box>
      </button>
    </>
  );
};
